// Recurring-coaching purchase logic: duration-variant selection, single-item
// Ajax Cart API submission, and the mobile sticky CTA. One shared static
// selection state drives every coaching-offer-purchase instance on the page
// (main section + sticky bar) so they can never drift out of sync. Mirrors
// the proven coaching-program.js pattern, adapted for an N-way duration
// choice instead of a base/combo binary choice.

if (!customElements.get('coaching-offer-purchase')) {
  customElements.define(
    'coaching-offer-purchase',
    class CoachingOfferPurchase extends HTMLElement {
      static instances = [];
      static variants = [];
      static selectedId = null;
      static isSubmitting = false;

      connectedCallback() {
        this.form = this.querySelector('form');
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.cta = this.querySelector('[data-purchase-cta]');
        this.statusEl = this.querySelector('[data-purchase-status]');
        this.errorEl = this.querySelector('[data-purchase-error]');
        this.summaryEl = this.querySelector('[data-purchase-summary]');

        const dataScript = this.querySelector('[data-coaching-offer-variants]');
        if (dataScript && !CoachingOfferPurchase.variants.length) {
          try {
            const parsed = JSON.parse(dataScript.textContent);
            if (Array.isArray(parsed)) CoachingOfferPurchase.variants = parsed;
          } catch (error) {
            // Leave variants empty - submit() will show a clear error rather
            // than silently failing.
          }
        }

        if (!CoachingOfferPurchase.selectedId) {
          const checkedRadio = this.radios.find((radio) => radio.checked);
          CoachingOfferPurchase.selectedId =
            checkedRadio?.value ?? CoachingOfferPurchase.variants[0]?.id ?? null;
        }

        CoachingOfferPurchase.instances.push(this);

        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => {
            CoachingOfferPurchase.setSelection(radio.value);
          });
        });

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          CoachingOfferPurchase.submit();
        });

        this.render();
      }

      disconnectedCallback() {
        CoachingOfferPurchase.instances = CoachingOfferPurchase.instances.filter((instance) => instance !== this);
      }

      render() {
        const selected = CoachingOfferPurchase.variants.find(
          (variant) => variant.id === CoachingOfferPurchase.selectedId
        );

        this.radios.forEach((radio) => {
          const isChecked = radio.value === CoachingOfferPurchase.selectedId;
          radio.checked = isChecked;
          radio.closest('.coaching-offer-purchase__option')?.classList.toggle('is-selected', isChecked);
        });

        if (!selected) return;
        if (this.cta) this.cta.textContent = selected.ctaLabel;
        if (this.summaryEl) this.summaryEl.textContent = `${selected.label} — ${selected.price} (one-time payment)`;
      }

      static setSelection(id) {
        CoachingOfferPurchase.selectedId = id;
        CoachingOfferPurchase.instances.forEach((instance) => instance.render());
      }

      static setBusy(isBusy) {
        CoachingOfferPurchase.instances.forEach((instance) => {
          if (!instance.cta) return;
          if (isBusy) {
            instance.cta.dataset.restoreLabel = instance.cta.textContent;
            instance.cta.textContent = 'Preparing your coaching…';
          } else if (instance.cta.dataset.restoreLabel) {
            instance.cta.textContent = instance.cta.dataset.restoreLabel;
            delete instance.cta.dataset.restoreLabel;
          }
          instance.cta.setAttribute('aria-busy', String(isBusy));
          instance.cta.disabled = isBusy;
          if (instance.statusEl) instance.statusEl.hidden = !isBusy;
        });
      }

      static showError(message) {
        CoachingOfferPurchase.instances.forEach((instance) => {
          if (!instance.errorEl) return;
          instance.errorEl.textContent = message;
          instance.errorEl.hidden = false;
        });
      }

      static clearError() {
        CoachingOfferPurchase.instances.forEach((instance) => {
          if (instance.errorEl) instance.errorEl.hidden = true;
        });
      }

      static async submit() {
        if (CoachingOfferPurchase.isSubmitting) return;

        CoachingOfferPurchase.clearError();

        const selected = CoachingOfferPurchase.variants.find(
          (variant) => variant.id === CoachingOfferPurchase.selectedId
        );

        if (!selected || !selected.available) {
          CoachingOfferPurchase.showError("This coaching option isn't available right now. Please try again.");
          return;
        }

        CoachingOfferPurchase.isSubmitting = true;
        CoachingOfferPurchase.setBusy(true);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: selected.id, quantity: 1 }] }),
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            throw new Error(payload?.description || "This coaching option isn't available right now. Please try again.");
          }

          window.location.href = '/cart';
        } catch (error) {
          CoachingOfferPurchase.showError(
            error.message || "This coaching option isn't available right now. Please try again."
          );
          CoachingOfferPurchase.isSubmitting = false;
          CoachingOfferPurchase.setBusy(false);
        }
      }
    }
  );
}

// Mobile sticky CTA: appears only once the visitor scrolls past the main
// purchase section, hides while it's visible, and disables itself once a
// submission is in progress. Mirrors coaching-program.js exactly.
(function () {
  function init() {
    const sticky = document.querySelector('[data-coaching-offer-sticky]');
    const mainPurchase = document.querySelector('[data-purchase-placement="main"]');
    if (!sticky || !mainPurchase || sticky.dataset.enabled === undefined) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const mainVisible = entries[0].isIntersecting;
        sticky.classList.toggle('is-visible', !mainVisible);
      },
      { threshold: 0 }
    );
    observer.observe(mainPurchase);

    const stickyForm = sticky.querySelector('coaching-offer-purchase');
    stickyForm?.addEventListener('submit', () => {
      sticky.classList.remove('is-visible');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Media gallery: native horizontal scroll + scroll-snap does the swiping,
// this only keeps the position dots in sync via IntersectionObserver
// (no library, no scroll-position math).
(function () {
  function init() {
    document.querySelectorAll('[data-coaching-offer-gallery]').forEach((gallery) => {
      const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
      const dotsWrap = gallery.parentElement?.querySelector('[data-gallery-dots]');
      const dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('[data-gallery-dot]')) : [];
      if (!slides.length || !dots.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = slides.indexOf(entry.target);
            dots.forEach((dot, dotIndex) => {
              const isActive = dotIndex === index;
              dot.classList.toggle('is-active', isActive);
              dot.setAttribute('aria-selected', String(isActive));
            });
          });
        },
        { root: gallery, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));

      gallery.querySelectorAll('.coaching-offer-hero__image--lazy').forEach((img) => {
        if (img.complete) {
          img.classList.add('is-loaded');
        } else {
          img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        }
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          slides[index]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// FAQ accordion: accessible, keyboard-operable, no framework. Collapsed
// panels are marked `inert` in addition to the visual clip, so their
// content is removed from the accessibility tree and tab order while
// closed. Mirrors coaching-program.js exactly.
(function () {
  function init() {
    document.querySelectorAll('[data-faq-trigger]').forEach((trigger) => {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      panel?.setAttribute('inert', '');

      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (!panel) return;
        if (expanded) {
          panel.removeAttribute('data-open');
          panel.setAttribute('inert', '');
        } else {
          panel.setAttribute('data-open', '');
          panel.removeAttribute('inert');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
